"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/src/lib/utils";
import { isOptimizableImage } from "@/src/shared/utils/optimizableImage";

/**
 * The fanned photo tiles of a rail's "See all" card, measured off the reference site:
 * each tile is the stack's width, turned and pushed apart, springing up into place
 * (`animate-see-all-tile`) the first time the stack comes into view, top tile first.
 * Bottom to top in paint order.
 */
const TILES = {
  bottom: {
    translate: "-17.14% -42.86%",
    rotate: "-2.5deg",
    from: "22.5deg",
    delay: "0.2s",
  },
  middle: {
    translate: "37.14% -25.71%",
    rotate: "5deg",
    from: "-20deg",
    delay: "0.1s",
  },
  top: {
    translate: "-37.14% -5.71%",
    rotate: "-5deg",
    from: "20deg",
    delay: "0s",
  },
  /** A lone tile: centred where the stack's middle would be, a slight turn. */
  single: { translate: "0 -22%", rotate: "-3deg", from: "20deg", delay: "0s" },
};

interface IPhotoStackProps {
  /**
   * One to three photo URLs, the first on top: one is a single centred tile, two a pair
   * (the middle and top tiles), three the full stack.
   */
  photos: string[];
  /** Stays beyond the tiles: a "+N" pill at the stack's right edge (none when 0). */
  more?: number;
  /** Sizes the stack (each tile is this wide), e.g. `w-[35%]`. */
  className?: string;
  /** The `sizes` hint for each tile's image. */
  imageSizes?: string;
}

/** Photos fanned out as tiles: the "See all" card's stack, also a wishlist folder's cover. */
const PhotoStack: React.FC<IPhotoStackProps> = ({
  photos,
  className,
  imageSizes = "80px",
  more = 0,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // The tiles spring in once, when the stack is first seen.
  useEffect(() => {
    const stack = ref.current;
    if (!stack || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setInView(true);
      },
      { threshold: 0.5 },
    );
    observer.observe(stack);
    return () => observer.disconnect();
  }, [inView]);

  // Bottom to top, so the first photo ends up on top.
  const tiles =
    photos.length >= 3
      ? [
          { ...TILES.bottom, photo: photos[2] },
          { ...TILES.middle, photo: photos[1] },
          { ...TILES.top, photo: photos[0] },
        ]
      : photos.length === 2
        ? [
            { ...TILES.middle, photo: photos[1] },
            { ...TILES.top, photo: photos[0] },
          ]
        : [{ ...TILES.single, photo: photos[0] }];

  return (
    <div ref={ref} className={cn("relative grid aspect-square", className)}>
      {tiles.map((tile, index) => (
        <div
          key={tile.translate}
          aria-hidden
          className={cn(
            "relative overflow-hidden rounded-[9px] border-[3px] border-white bg-[#8C8C8C] shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_2px_4px_rgba(0,0,0,0.16)] [grid-area:1/1]",
            inView ? "animate-see-all-tile" : "scale-0",
          )}
          style={
            {
              zIndex: index + 1,
              animationDelay: tile.delay,
              "--tile-translate": tile.translate,
              "--tile-rotate": tile.rotate,
              "--tile-rotate-from": tile.from,
            } as React.CSSProperties
          }
        >
          {/* Through Next's image resizer, as the cards' photos (a raw CDN URL in a
              background did not load). */}
          {tile.photo && (
            <Image
              src={tile.photo}
              alt=""
              fill
              sizes={imageSizes}
              className="object-cover"
              unoptimized={!isOptimizableImage(tile.photo)}
            />
          )}
        </div>
      ))}
      {/* How many stays the tiles leave out: a dark count pinned to the right tile's
          lower corner, in after the tiles have landed. Neutral, not an alert. */}
      {more > 0 && (
        <span
          aria-label={`${more} more ${more === 1 ? "stay" : "stays"}`}
          className={cn(
            "absolute left-[118%] top-[52%] z-10 flex h-7 min-w-7 items-center justify-center rounded-full bg-ink px-2 text-12 font-semibold text-white ring-2 ring-white transition-opacity duration-300",
            inView ? "opacity-100 delay-500" : "opacity-0",
          )}
        >
          +{more}
        </span>
      )}
    </div>
  );
};

export default PhotoStack;
